import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import {
  ThemeProvider as MuiThemeProvider,
  theme as MuiTheme,
} from '@locus-taxy/encore-mui';
import { ToastProvider } from '@locus-taxy/encore-ui-toolkit';
import { LicenseInfo } from '@locus-taxy/encore-mui/x-license';
import { I18nProvider } from '@locus-taxy/encore-i18n-toolkit';
import { Auth0Provider } from './auth';
import App from './App';
import { NetworkProvider } from './api/network/NetworkProvider';
import { UserProvider } from './contexts/UserContext';
import SuspenseLoader from './components/SuspenseLoader';

import { ErrorBoundary } from './components/ErrorBoundary';
import fallbackStrings from './strings.json';
import { useClientId } from './hooks/useClientId';
import { GridEntityPickerProvider } from '@locus-taxy/encore-entity-picker-grid';
import { AUTH0_AUDIENCE, AUTH0_CLIENT_ID, AUTH0_DOMAIN } from './config';

import { initBrowserO11y } from './utils/o11yUtils';
import { initBugsnag } from './utils/bugsnag';

// Wrapper component to handle Auth0 redirect callback with React Router
export function AppWrapper() {
  const navigate = useNavigate();
  const resolvedClientId = useClientId();

  return (
    <MuiThemeProvider theme={MuiTheme}>
      <GridEntityPickerProvider>
        <I18nProvider fallbackStrings={fallbackStrings}>
          <ToastProvider>
            <ErrorBoundary>
              <Auth0Provider
                domain={AUTH0_DOMAIN}
                clientId={AUTH0_CLIENT_ID}
                authorizationParams={{
                  redirect_uri: window.location.origin,
                  audience: AUTH0_AUDIENCE,
                  l_client_id: resolvedClientId,
                }}
                useRefreshTokens
                useRefreshTokensFallback
                onRedirectCallback={appState => {
                  const requestedPath =
                    appState?.returnTo ?? window.location.pathname;
                  navigate(requestedPath, { replace: true });
                }}
              >
                <UserProvider>
                  <NetworkProvider>
                    <Suspense fallback={<SuspenseLoader />}>
                      <App />
                    </Suspense>
                  </NetworkProvider>
                </UserProvider>
              </Auth0Provider>
            </ErrorBoundary>
          </ToastProvider>
        </I18nProvider>
      </GridEntityPickerProvider>
    </MuiThemeProvider>
  );
}

LicenseInfo.setLicenseKey(
  '45e898c6e122458d37c25279e6478ad5Tz0xMjY5NDksRT0xODA1MDY4Nzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMy0yMDI0LEtWPTI='
);

initBugsnag();
initBrowserO11y();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AppWrapper />
    </BrowserRouter>
  </StrictMode>
);
